import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="oms--oms--filter-sidebar"
export default class extends Controller {
  static targets = ["searchInput", "selectionSummary", "loadingIndicator"]
  static values = {
    currentFilters: Object
  }

  connect() {
    this.lastSubmittedUrl = null
    this.submitTimeout = null
    this.isSubmitting = false
    this.initializationAttempts = 0
    this.maxInitializationAttempts = 10

    // Use setTimeout to ensure DOM is fully ready
    setTimeout(() => {
      this.updateSelectionSummary()
      this.initializeStatusFiltersState()
      // Initialize search icon updates
      this.initializeSearchIconUpdates()
    }, 100)

    // Initialize auto-submit with retry mechanism
    this.initializeWithRetry()
  }

  initializeWithRetry() {
    const form = this.element.querySelector('#quotes-filter-form')
    if (!form && this.initializationAttempts < this.maxInitializationAttempts) {
      this.initializationAttempts++
      setTimeout(() => {
        this.initializeWithRetry()
      }, 100)
      return
    }

    if (!form) {
      console.log('Form not found after maximum retry attempts')
      return
    }

    console.log('Form found, initializing auto-submit')
  }

  disconnect() {
    if (this.submitTimeout) {
      clearTimeout(this.submitTimeout)
    }
    console.log('Filter sidebar controller disconnected')
  }

  // Method to re-initialize after Turbo Stream updates
  reinitialize() {
    console.log('Reinitializing filter sidebar controller')
    this.initializationAttempts = 0
    // Re-initialize search icon updates
    this.initializeSearchIconUpdates()
    this.initializeWithRetry()
  }

  handleFilterChange(event) {
    console.log('Filter change detected:', event?.target?.name, event?.target?.value)
    
    // Update selection summary immediately
    this.updateSelectionSummary()

    // Prevent multiple submissions if one is already in progress
    if (this.isSubmitting) {
      console.log('Already submitting, skipping...')
      return
    }

    // Clear existing timeout
    if (this.submitTimeout) {
      clearTimeout(this.submitTimeout)
    }

    // Set new timeout for 500ms debounce
    this.submitTimeout = setTimeout(() => {
      console.log('Submitting form after debounce...')
      this.isSubmitting = true
      this.showLoadingIndicator()

      // Ensure DOM updates are complete before submitting
      setTimeout(() => {
        this.submitForm()
        // Reset flag after submission
        setTimeout(() => {
          this.isSubmitting = false
          this.hideLoadingIndicator()
        }, 100)
      }, 50)
    }, 500)
  }

  submitForm() {
    console.log('submitForm called')
    const form = document.getElementById('quotes-filter-form')
    if (!form) {
      console.error('Form not found: quotes-filter-form')
      // Try to find form in the current element
      const formInElement = this.element.querySelector('#quotes-filter-form')
      if (formInElement) {
        console.log('Found form in element, submitting...')
        this.updateUrlOnSubmit(formInElement)
        this.submitWithTurboStream(formInElement)
        return
      }
      return
    }

    console.log('Form found, collecting form data...')
    // Log form data for debugging
    const formData = new FormData(form)
    console.log('Form data entries:')
    for (let [key, value] of formData.entries()) {
      console.log(`${key}: ${value}`)
    }

    // Clear status filters if not on "All" tab
    this.clearStatusFiltersIfNeeded(form)

    // Update URL before submitting
    this.updateUrlOnSubmit(form)

    // Submit the form with Turbo Stream
    this.submitWithTurboStream(form)
  }

  submitWithTurboStream(form) {
    // Use fetch with Turbo Stream headers to prevent page reload
    const formData = new FormData(form)

    // Ensure status parameter is included if it exists in the URL
    const urlParams = new URLSearchParams(window.location.search)
    const currentStatus = urlParams.get('status')
    if (currentStatus) {
      formData.append('status', currentStatus)
    }

    const url = form.action + '?' + new URLSearchParams(formData).toString()

    fetch(url, {
      method: 'GET',
      headers: {
        'Accept': 'text/vnd.turbo-stream.html, text/html, application/xhtml+xml',
        'X-Requested-With': 'XMLHttpRequest',
        'Turbo-Frame': 'quotes_data'
      }
    })
    .then(response => {
      console.log('Response status:', response.status)
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      return response.text()
    })
    .then(html => {
      console.log('Received HTML:', html.substring(0, 200) + '...')

      // Check if the response contains turbo-stream tags
      if (html.includes('<turbo-stream')) {
        console.log('Turbo stream detected in response')
        // Parse and execute the Turbo Stream
        if (typeof Turbo !== 'undefined' && Turbo.renderStreamMessage) {
          console.log('Rendering turbo stream...')
          Turbo.renderStreamMessage(html)
        } else {
          console.error('Turbo is not available')
          // Fallback: reload the page
          window.location.reload()
        }
      } else {
        console.warn('No turbo-stream tags found in response')
        console.log('Response preview:', html.substring(0, 500))
      }
    })
    .catch(error => {
      console.error('Error submitting form:', error)
      // Fallback: try regular form submission
      console.log('Attempting fallback form submission...')
      form.submit()
    })
  }

  updateUrlOnSubmit(form) {
    const formData = new FormData(form)
    const params = new URLSearchParams()

    // Filter out empty values before adding to URL params
    for (const [key, value] of formData.entries()) {
      console.log(`${key}: ${value}`)
      if (value !== null && value !== undefined && value !== '') {
        params.append(key, value)
      }
    }

    const queryString = params.toString()
    const newUrl = queryString ? `${window.location.pathname}?${queryString}` : window.location.pathname

    if (this.lastSubmittedUrl !== newUrl) {
      this.lastSubmittedUrl = newUrl
      window.history.pushState({}, '', newUrl)
    }
  }

  submitFormIfNeeded() {
    const currentUrl = window.location.search

    // Prevent infinite loops by checking if we've already submitted for this URL
    if (this.lastSubmittedUrl === currentUrl) {
      return
    }

    const urlParams = new URLSearchParams(currentUrl)
    const hasFilters = urlParams.has('search') ||
                      urlParams.has('start_date') ||
                      urlParams.has('end_date') ||
                      urlParams.has('settlement_account_id') ||
                      urlParams.has('filter_status')

    if (hasFilters) {
      // Use a longer delay to ensure everything is properly initialized
      setTimeout(() => {
        this.lastSubmittedUrl = currentUrl
        this.submitForm()
      }, 300)
    }
  }

  showLoadingIndicator() {
    if (this.hasLoadingIndicatorTarget) {
      this.loadingIndicatorTarget.classList.remove('hidden')
    }
  }

  hideLoadingIndicator() {
    if (this.hasLoadingIndicatorTarget) {
      this.loadingIndicatorTarget.classList.add('hidden')
    }
  }

  applyFilters(event) {
    // Form submission will be handled by Rails, but we can add loading states here
    this.showClearButtonLoading()
    const submitButton = event.target.querySelector('input[type="submit"]')
    if (submitButton) {
      submitButton.value = "Applying..."
      submitButton.disabled = true
    }
  }

  updateSelectionSummary() {
    if (!this.hasSelectionSummaryTarget) return

    const activeFilters = this.getActiveFilters()
    let summary = "All quotes"

    if (activeFilters.length > 0) {
      summary = `Filtered by: ${activeFilters.join(', ')}`
    }

    this.selectionSummaryTarget.textContent = summary
  }

  getActiveFilters() {
    const filters = []

    // Check search input
    if (this.hasSearchInputTarget && this.searchInputTarget.value.trim()) {
      filters.push(`search: "${this.searchInputTarget.value.trim()}"`)
    }

    // Check selected status checkboxes (excluding "All" which has empty value)
    const selectedStatuses = []
    this.element.querySelectorAll('input[name="filter_status[]"]:checked').forEach(checkbox => {
      if (checkbox.value !== '') {
        selectedStatuses.push(checkbox.value)
      }
    })

    if (selectedStatuses.length > 0) {
      filters.push(`status: ${selectedStatuses.join(', ')}`)
    }

    // Check selected type pills
    const selectedTypes = []
    this.element.querySelectorAll('input[name="filter_type[]"]:checked').forEach(checkbox => {
      selectedTypes.push(checkbox.value)
    })

    if (selectedTypes.length > 0) {
      filters.push(`type: ${selectedTypes.join(', ')}`)
    }

    // Check date range
    const startDate = this.element.querySelector('input[name="start_date"]')?.value
    const endDate = this.element.querySelector('input[name="end_date"]')?.value

    if (startDate || endDate) {
      let dateRange = "date range"
      if (startDate && endDate) {
        dateRange = `${startDate} to ${endDate}`
      } else if (startDate) {
        dateRange = `from ${startDate}`
      } else if (endDate) {
        dateRange = `until ${endDate}`
      }
      filters.push(dateRange)
    }

    return filters
  }

    // Handle status tab changes from the status tabs controller
  handleStatusTabChange(status) {
    if (status === 'all') {
      this.enableStatusFilters()
    } else {
      this.disableStatusFilters()
      this.resetStatusFilters()
    }
  }

    // Enable status filters (only for "All" tab)
  enableStatusFilters() {
    const statusFilterSection = this.element.querySelector('.status-filters-section')
    if (statusFilterSection) {
      statusFilterSection.classList.remove('opacity-50', 'pointer-events-none')
    }

    // Enable the multiselect component
    const statusMultiselect = this.element.querySelector('[data-controller*="uikit--select-multi"]')
    if (statusMultiselect) {
      const multiselectController = this.application.getControllerForElementAndIdentifier(
        statusMultiselect,
        'uikit--select-multi'
      )
      if (multiselectController && multiselectController.enable) {
        multiselectController.enable()
      }
    }
  }

      // Disable status filters (for non-"All" tabs)
  disableStatusFilters() {
    const statusFilterSection = this.element.querySelector('.status-filters-section')
    if (statusFilterSection) {
      statusFilterSection.classList.add('opacity-50', 'pointer-events-none')
    }

    // Disable the multiselect component
    const statusMultiselect = this.element.querySelector('[data-controller*="uikit--select-multi"]')
    if (statusMultiselect) {
      const multiselectController = this.application.getControllerForElementAndIdentifier(
        statusMultiselect,
        'uikit--select-multi'
      )
      if (multiselectController && multiselectController.disable) {
        multiselectController.disable()
      }
    }
  }

  // Reset status filters
  resetStatusFilters() {
    // Clear the multiselect component
    const statusMultiselect = this.element.querySelector('[data-controller*="uikit--select-multi"]')
    if (statusMultiselect) {
      const multiselectController = this.application.getControllerForElementAndIdentifier(
        statusMultiselect,
        'uikit--select-multi'
      )
      if (multiselectController && multiselectController.clear) {
        multiselectController.clear()
      }
    }
  }

  // Initialize status filters state based on current URL
  initializeStatusFiltersState() {
    const urlParams = new URLSearchParams(window.location.search)
    const currentStatus = urlParams.get('status')

    if (currentStatus && currentStatus !== 'all') {
      this.disableStatusFilters()
      this.resetStatusFilters()
    } else {
      this.enableStatusFilters()
    }
  }

  // Clear status filters from form data if not on "All" tab
  clearStatusFiltersIfNeeded(form) {
    const urlParams = new URLSearchParams(window.location.search)
    const currentStatus = urlParams.get('status')

    if (currentStatus && currentStatus !== 'all') {
      // Clear the multiselect component
      const statusMultiselect = this.element.querySelector('[data-controller*="uikit--select-multi"]')
      if (statusMultiselect) {
        const multiselectController = this.application.getControllerForElementAndIdentifier(
          statusMultiselect,
          'uikit--select-multi'
        )
        if (multiselectController && multiselectController.clear) {
          multiselectController.clear()
        }
      }
    }
  }

  // Initialize search icon updates
  initializeSearchIconUpdates() {
    const quotesInput = document.getElementById('quotes-search-input')
    if (quotesInput) {
      this.updateQuotesSearchIcon(quotesInput.value)
    }
  }

  // Handle search input changes
  handleSearchInputChange(event) {
    this.updateQuotesSearchIcon(event.target.value)
  }

  // Update search icon visibility
  updateQuotesSearchIcon(value) {
    const clearBtn = document.getElementById('clear-quotes-search-btn')
    const searchIcon = document.getElementById('quotes-search-icon')

    if (clearBtn && searchIcon) {
      if (value.trim() === '') {
        clearBtn.classList.add('hidden')
        searchIcon.classList.remove('hidden')
      } else {
        clearBtn.classList.remove('hidden')
        searchIcon.classList.remove('hidden')
      }
    }
  }

  // Clear search function
  clearQuotesSearch() {
    console.log('Clearing quotes search...')
    const input = document.getElementById('quotes-search-input')
    if (input) {
      input.value = ''
      this.updateQuotesSearchIcon('')

      // Trigger the form submission to update results
      const form = document.getElementById('quotes-filter-form')
      if (form) {
        console.log('Triggering form submission after clearing search...')
        // Use the same method as handleFilterChange to ensure URL is updated
        this.handleFilterChange()
      }
    }
  }

  handleClearFilters(event) {
    event.preventDefault()

    this.showClearButtonLoading()
    
    // Build reset URL - preserve only the status parameter
    const currentUrl = new URL(window.location)
    const currentParams = currentUrl.searchParams
    const resetParams = new URLSearchParams()
    
    // Preserve the status parameter to maintain the current tab
    const currentStatus = currentParams.get('status')
    if (currentStatus) {
      resetParams.set('status', currentStatus)
    }
    
    const resetUrl = `${currentUrl.pathname}?${resetParams.toString()}`
    
    // Use Turbo Stream to update the quotes data
    fetch(resetUrl, {
      method: 'GET',
      headers: {
        'Accept': 'text/vnd.turbo-stream.html, text/html, application/xhtml+xml',
        'X-Requested-With': 'XMLHttpRequest',
        'Turbo-Frame': 'quotes_data'
      }
    })
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      return response.text()
    })
    .then(html => {
      // Update the browser URL
      window.history.pushState({}, '', resetUrl)
      
      // Parse and execute the Turbo Stream
      if (html.includes('<turbo-stream') && typeof Turbo !== 'undefined' && Turbo.renderStreamMessage) {
        Turbo.renderStreamMessage(html)
      } else {
        console.warn('No turbo-stream tags found in response')
        window.location.reload()
      }
    })
    .catch(error => {
      console.error('Error resetting filters:', error)
      // Fallback: redirect to reset URL
      window.location.href = resetUrl
    })
  }

  showClearButtonLoading() {
    const clearButton = document.getElementById('clear-filters-button')
    const spinnerButton = document.getElementById('clear-filters-spinner')

    if (clearButton && spinnerButton) {
      clearButton.classList.add('!hidden')
      spinnerButton.classList.remove('!hidden')
    }
  }
}
